import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  SelectorContainer,
  DhamSelector,
  DhamButton,
  CabTypeSelector,
  CabButton,
  McPackageScroller,
  McPackageWindow,
  Card,
  CardInfo,
  McPackageImage,
  CarInfo,
  CarName,
  CarSeats,
  Title,
  Duration,
  Dham,
  StopText,
  PriceTextSup,
  Price,
  ViewCab,
  BoxImage,
  Selectors,
  TitleText,
  VerticalFlex,
  DhamText,
  HrLine,
  BookCabContainer,
  BookCabStyle,
  CabTypeText,
  ViewCabText,
  PriceText,
} from "./MultiCityPackages.styles";
import { CHAR_DHAM_IMAGES } from "../../../../../shared/config/constants";
import LeftArrowIcon from "./LeftArrowIcon";
import RightArrowIcon from "./RightArrowIcon";

const ArrowButton = styled.button`
  background: white;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ right }) => (right ? "right: -20px;" : "left: -20px;")}

  svg {
    fill: #2276e3;
    width: 24px;
    height: 24px;
  }
`;

const ScrollerWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const MultiCityPackages = ({ packages = [], trackEvent }) => {

  const initCab = () => {
    if (
      packages &&
      packages
        .map((pkg) => pkg?.cab_type)
        .filter((pkg) => pkg)
        .includes("Sedan")
    ) {
      return "Sedan";
    } else {
      return "SUV";
    }
  };

  const initDham = () => {
    return Math.min(...packages.map((pkg) => pkg?.dham));
  };

  const [cabType, setCabType] = useState(initCab());
  const [dhamType, setDham] = useState(initDham());
  const scrollerRef = useRef(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (scrollerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollerRef.current;
        setIsAtStart(scrollLeft === 0);
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1); // Small adjustment for precision
      }
    };

    const checkIfScrollable = () => {
      if (scrollerRef.current) {
        const { scrollWidth, clientWidth } = scrollerRef.current;
        // Hide both arrows if the content fits within the container
        if (scrollWidth <= clientWidth) {
          setIsAtStart(true);
          setIsAtEnd(true);
        } else {
          setIsAtStart(scrollerRef.current.scrollLeft === 0);
          setIsAtEnd(
            scrollerRef.current.scrollLeft + clientWidth >= scrollWidth - 1
          );
        }
      }
    };

    if (scrollerRef.current) {
      scrollerRef.current.addEventListener("scroll", handleScroll);
      checkIfScrollable(); // Check on component mount if it's scrollable
    }

    window.addEventListener("resize", checkIfScrollable); // Check on window resize

    return () => {
      if (scrollerRef.current) {
        scrollerRef.current.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("resize", checkIfScrollable);
    };
  }, [dhamType, cabType]);

  const handleScroll = (direction) => {
    if (scrollerRef.current) {
      const scrollAmount = 500;
      scrollerRef.current.scrollLeft +=
        direction === "left" ? -scrollAmount : scrollAmount;
    }
  };

  const getDham = () => {
    return Array.from(new Set(packages.map(({ dham }) => `${dham}`))).sort();
  };

  const getCabType = () => {
    return Array.from(
      new Set(
        packages
          .map(({ cab_type }) => `${cab_type}`)
          .filter((cab_type) => String(cab_type).toUpperCase() !== "HATCHBACK")
      )
    );
  };

  const updateUrlWithNextDayAndGap = (url, days) => {
    const qryParamsPart = url.split("qryParams=")[1];

    if (!qryParamsPart) {
      console.error("Invalid URL: qryParams not found");
      return url;
    }

    const decodedParams = decodeURIComponent(qryParamsPart);
    let qryParamsObject;

    try {
      qryParamsObject = JSON.parse(decodedParams);
    } catch (error) {
      console.error("Failed to parse JSON: ", error);
      return url;
    }

    const currentDate = new Date();
    const nextDay = new Date(currentDate);
    nextDay.setDate(currentDate.getDate() + 1);
    nextDay.setUTCHours(10, 0, 0, 0);
    const formattedStartDay = nextDay.toISOString().slice(0, 16);

    const endDay = new Date(nextDay);
    endDay.setDate(nextDay.getDate() + days);
    endDay.setUTCHours(10, 0, 0, 0);
    const formattedEndDay = endDay.toISOString().slice(0, 16);

    qryParamsObject.s_t = formattedStartDay;
    qryParamsObject.e_t = formattedEndDay;

    const updatedQryParams = encodeURIComponent(
      JSON.stringify(qryParamsObject)
    );
    return `${url.split("qryParams=")[0]}qryParams=${updatedQryParams}`;
  };

  return (
    <SelectorContainer>
      <Selectors>
        <DhamSelector>
          {getDham().map((dham) => (
            <DhamButton
              selected={Number(dham) === dhamType}
              key={dham}
              onClick={() => {
                trackEvent(`${dham}_Dham_selected`);
                setDham(Number(dham));
              }}
            >
              {dham} Dham
            </DhamButton>
          ))}
        </DhamSelector>
        <CabTypeSelector style={{ display: "none" }}>
          {getCabType().map((cabtype) => (
            <CabButton
              key={cabtype}
              onClick={() => {
                trackEvent(`${cabtype}_cab_type_selected`);
                setCabType(cabtype);
              }}
              selected={cabtype === cabType}
            >
              <BoxImage
                src={`${
                  cabtype === "Suv"
                    ? `${CHAR_DHAM_IMAGES.SUV}`
                    : `${CHAR_DHAM_IMAGES.SEDAN}`
                }`}
                alt={cabtype}
              />
              <CardInfo>
                <CarName>{cabtype}</CarName>
                <CarSeats>4 seater</CarSeats>
              </CardInfo>
              {cabtype === cabType ? (
                <img
                  style={{
                    width: "20px",
                    marginLeft: "auto",
                    marginRight: "5px",
                  }}
                  src={`${CHAR_DHAM_IMAGES.RIGHT_ICON}`}
                  alt="Selected"
                />
              ) : null}
            </CabButton>
          ))}
        </CabTypeSelector>
      </Selectors>
      <ScrollerWrapper>
        {!isAtStart && (
          <ArrowButton
            onClick={() => handleScroll("left")}
            aria-label="Previous Package"
          >
            <LeftArrowIcon />
          </ArrowButton>
        )}
        <McPackageScroller ref={scrollerRef}>
          <McPackageWindow>
            {packages.map(
              (
                {
                  title,
                  dham,
                  dham_included,
                  cab_type,
                  source,
                  duration,
                  price_text,
                  price_text_sub,
                  stops = [],
                  gi_img,
                  gi_url,
                },
                idx
              ) => {
                return cab_type === cabType && dham === dhamType ? (
                  <Card
                    key={idx}
                    onClick={() => {
                      trackEvent(
                        `${cabType}_${dhamType}_${title}_view_cab_click`
                      );
                      window.location = Object(
                        updateUrlWithNextDayAndGap(gi_url, duration)
                      );
                    }}
                  >
                    <CardInfo>
                      <McPackageImage src={gi_img} alt={title} />
                      <Title>
                        <TitleText>{title}</TitleText>
                        <Duration>{`${duration} days`}</Duration>
                      </Title>
                      <Dham>
                        <div>
                          <VerticalFlex>
                            <img
                              width={20}
                              height={20}
                              src={`${CHAR_DHAM_IMAGES.LOCATION_ICON}`}
                              alt="Source"
                            />
                            <DhamText>{source}</DhamText>
                          </VerticalFlex>
                          {stops &&
                            stops.length > 0 &&
                            stops.map((stop) => (
                              <VerticalFlex key={stop}>
                                <img
                                  width={20}
                                  height={20}
                                  src={`${CHAR_DHAM_IMAGES.STOPS_ICON}`}
                                  alt="Stop"
                                />
                                <StopText
                                  style={{
                                    fontWeight: dham_included[stop] ? 900 : 400,
                                  }}
                                >
                                  {stop}
                                </StopText>
                              </VerticalFlex>
                            ))}
                          <VerticalFlex>
                            <img
                              width={20}
                              height={20}
                              src={`${CHAR_DHAM_IMAGES.LOCATION_ICON}`}
                              alt="Source"
                            />
                            <DhamText>{source}</DhamText>
                          </VerticalFlex>
                        </div>
                      </Dham>
                      <div style={{ marginTop: "auto" }}>
                        <HrLine />
                        <BookCabContainer>
                          <BookCabStyle>
                            <PriceTextSup>
                              <span>{price_text_sub}</span>
                            </PriceTextSup>
                            <Price>
                              <PriceText>
                                {price_text}
                              </PriceText>
                              <CabTypeText>{`for ${cab_type}`}</CabTypeText>
                            </Price>
                          </BookCabStyle>
                          <ViewCab>
                            <ViewCabText>{"View Cabs"}</ViewCabText>
                          </ViewCab>
                        </BookCabContainer>
                      </div>
                    </CardInfo>
                  </Card>
                ) : null;
              }
            )}
          </McPackageWindow>
        </McPackageScroller>
        {!isAtEnd && (
          <ArrowButton
            right
            onClick={() => handleScroll("right")}
            aria-label="Next Package"
          >
            <RightArrowIcon />
          </ArrowButton>
        )}
      </ScrollerWrapper>
    </SelectorContainer>
  );
};

export default MultiCityPackages;
